import * as React from "react"
import { useHistory, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  LayoutDashboard,
  Users,
  FileText,
  Gift,
  MinusCircle,
  Timer,
  CalendarOff,
  ArrowLeftRight,
  AlarmClock,
  ChevronRight,
  Clock,
  Calculator,
  Banknote,
  Settings,
  CalendarDays,
  History,
} from "lucide-react"

import { NavUser } from "./nav-user"
import { SearchForm } from "./search-form"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from "../ui/sidebar"
import { getDisplayVersion } from "../common/VersionDisplay"

export function AppSidebar({ user, onLogout, ...props }) {
  const { t } = useTranslation("common")
  const history = useHistory()
  const location = useLocation()
  const contentRef = React.useRef(null)
  const permissionsReady = Array.isArray(user?.permissions) || Array.isArray(user?.permission)
  const userPermissions = React.useMemo(() => {
    if (!permissionsReady) return []
    if (Array.isArray(user?.permissions)) return user.permissions
    if (Array.isArray(user?.permission)) return user.permission
    return []
  }, [permissionsReady, user])

  const canAccess = React.useCallback(
    (requiredPermissions = [], mode = "any") => {
      if (!permissionsReady) return true
      if (!requiredPermissions || requiredPermissions.length === 0) return true
      if (mode === "all") {
        return requiredPermissions.every((permission) => userPermissions.includes(permission))
      }
      return requiredPermissions.some((permission) => userPermissions.includes(permission))
    },
    [permissionsReady, userPermissions]
  )

  // Navigation data organized by groups
  const navGroups = React.useMemo(() => {
    const settingsLandingPermissions = [
      "salary.view",
      "salary.update",
      "system.config",
      "setting.department",
      "setting.department.view",
      "setting.position",
      "setting.position.view",
      "setting.leave_type",
      "setting.leave_type.view",
      "setting.salary_component",
      "setting.salary_component.view",
      "setting.salary_template",
      "setting.salary_template.view",
      "setting.salary_template.create",
      "setting.salary_template.update",
      "setting.salary_template.delete",
      "setting.ai_camera",
      "system.user",
      "system.user.view",
      "system.role",
      "system.role.view",
      "system.permission",
      "system.permission.view",
    ]

    return [
      {
        label: t("menu.dashboard") || "Dashboard",
        items: [
          {
            title: t("menu.dashboard") || "Dashboard",
            url: "/dashboard",
            icon: LayoutDashboard,
            permissions: ["employee.view", "employee.view_team", "employee.view_self", "attendance.view", "payroll.view"],
            permissionsMode: "any",
          },
        ],
      },
      {
        label: t("menu.shift") || "Shift",
        items: [
          {
            title: t("menu.shift") || "Shift",
            url: "/shift",
            icon: CalendarDays,
             permissions: ["shift.view"],
            permissionsMode: "any",
          },
        ],
      },
      {
        label: t("menu.timekeeping") || "Timekeeping",
        items: [
          {
            title: t("menu.timekeeping") || "Timekeeping",
            url: "/timekeeping",
            icon: Clock,
            // Timekeeping module should only appear for attendance-related permissions.
            // `employee.view` is not sufficient to access timekeeping screens.
            permissions: ["attendance.view", "attendance.approve"],
            permissionsMode: "any",
          },
        ],
      },
      {
        label: t("menu.payroll_data") || "Payroll Data",
        items: [
          {
            title: t("menu.bonus") || "Bonus",
            url: "/payroll-data/bonus-list",
            icon: Gift,
            permissions: ["payroll-data.bonus-list.view", "payroll.view"],
          },
          {
            title: t("menu.deduction") || "Deduction",
            url: "/payroll-data/deduction-list",
            icon: MinusCircle,
            permissions: ["payroll-data.deduction-list.view", "payroll.view"],
          },
          {
            title: t("menu.overtime") || "Overtime",
            url: "/payroll-data/overtime",
            icon: Timer,
            permissions: ["overtime.view"],
          },
          {
            title: t("menu.leave_request") || "Leave Request",
            url: "/payroll-data/leave-request",
            icon: CalendarOff,
            permissions: ["leave.view"],
          },
          {
            title: t("menu.shift_change") || "Shift Change",
            url: "/payroll-data/shift-change",
            icon: ArrowLeftRight,
            permissions: ["shift_change.view"],
          },
          {
            title: t("menu.late_early") || "Late/Early",
            url: "/payroll-data/late-early",
            icon: AlarmClock,
            permissions: ["late_early.view"],
          },
          {
            title: t("menu.change_history") || "Change History",
            url: "/payroll-data/change-history",
            icon: History,
            permissions: [
              "payroll-data.bonus-list.view",
              "payroll-data.deduction-list.view",
              "overtime.view",
              "leave.view",
              "shift_change.view",
              "late_early.view",
              "payroll.view",
            ],
            permissionsMode: "any",
          },
        ],
      },
      {
        label: t("menu.payroll") || "Payroll",
        items: [
          {
            title: t("menu.payroll_calculation") || "Payroll Calculation",
            url: "/payroll-calculation",
            icon: Calculator,
            permissions: ["payroll-calculation.view", "payroll.view"],
          },
          {
            title: t("menu.payroll_payment") || "Payroll Payment",
            url: "/payroll-payment",
            icon: Banknote,
            permissions: ["payroll-payment.view", "payroll.view"],
          },
        ],
      },
      {
        label: t("menu.staff") || "Staff",
        items: [
          {
            title: t("menu.staff_list") || "Staff List",
            url: "/staff",
            icon: Users,
             permissions: ["employee.view"],
            permissionsMode: "any",
          },
        ],
      },
      {
        label: t("menu.settings") || "Settings",
        items: [
          {
            title: t("menu.settings") || "Settings",
            url: "/settings",
            icon: Settings,
            permissions: settingsLandingPermissions,
            permissionsMode: "any",
          },
        ],
      },
      {
        label: t("menu.report") || "Reports",
        items: [
          {
            title: t("menu.report") || "Reports",
            url: "/report",
            icon: FileText,
            permissions: ["report.attendance", "report.employee", "report.payroll", "report.export"],
            permissionsMode: "any",
          },
        ],
      },
    ]
  }, [t])

  const filteredNavGroups = React.useMemo(() => {
    const filterItems = (items) => {
      return items
        .map((item) => {
          const subItems = item.subItems ? filterItems(item.subItems) : null
          const hasPermission = item.permissions ? canAccess(item.permissions, item.permissionsMode) : true
          if (item.subItems) {
            if (!hasPermission || !subItems || subItems.length === 0) return null
            return { ...item, subItems }
          }
          return hasPermission ? item : null
        })
        .filter(Boolean)
    }

    return navGroups
      .map((group) => {
        const items = filterItems(group.items || [])
        if (!items || items.length === 0) return null
        return { ...group, items }
      })
      .filter(Boolean)
  }, [canAccess, navGroups])

  const userData = {
    name:
      user?.employee_profile?.full_name ||
      user?.employee_name ||
      (user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : "") ||
      user?.name ||
      user?.email ||
      user?.username ||
      "User",
    employeeCode:
      user?.employee_profile?.employee_code ||
      (user?.employee_profile || user?.employee_name || user?.employee_id
        ? user?.employee_code
        : "") ||
      "",
    email: user?.email || user?.username || "",
    avatar: user?.avatar || "",
  }

  const isActive = (url, exact = false) => {
    if (exact) {
      return location.pathname === url
    }
    if (url === "/dashboard") {
      return location.pathname === "/dashboard" || location.pathname === "/"
    }
    // For /timekeeping and /settings, use exact match to avoid highlighting parent when on sub-pages
    if (url === "/timekeeping" || url === "/settings") {
      return location.pathname === url
    }
    return location.pathname === url || location.pathname.startsWith(url + "/")
  }

  const isSubItemActive = (url) => {
    if (url === "/dashboard") {
      return location.pathname === "/dashboard" || location.pathname === "/"
    }
    return location.pathname === url
  }

  React.useEffect(() => {
    const container = contentRef.current
    if (!container) return

    const scrollToActive = () => {
      const activeButton = container.querySelector(
        '[data-sidebar="menu-button"][data-active="true"]'
      )
      if (!activeButton) return

      const containerRect = container.getBoundingClientRect()
      const activeRect = activeButton.getBoundingClientRect()
      const isVisible =
        activeRect.top >= containerRect.top && activeRect.bottom <= containerRect.bottom

      if (!isVisible) {
        activeButton.scrollIntoView({ block: "center" })
      }
    }

    const raf = window.requestAnimationFrame(scrollToActive)
    return () => window.cancelAnimationFrame(raf)
  }, [location.pathname, filteredNavGroups])

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              onClick={() => history.push("/dashboard")}
            >
              <div className="bg-primary text-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <Clock className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Mercury TNA</span>
                <span className="truncate text-xs">{getDisplayVersion()}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SearchForm />
      </SidebarHeader>
      <SidebarContent ref={contentRef}>
        {filteredNavGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  // If item has subItems, render collapsible
                  if (item.subItems && item.subItems.length > 0) {
                    return (
                      <Collapsible
                        key={item.title}
                        asChild
                        defaultOpen={isActive(item.url)}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                              tooltip={item.title}
                              isActive={isActive(item.url)}
                            >
                              {item.icon && <item.icon className="size-4" />}
                              <span>{item.title}</span>
                              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.subItems.map((subItem) => (
                                <SidebarMenuSubItem key={subItem.title}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={isSubItemActive(subItem.url)}
                                  >
                                    <a
                                      href={subItem.url}
                                      onClick={(e) => {
                                        e.preventDefault()
                                        history.push(subItem.url)
                                      }}
                                    >
                                      {subItem.icon && (
                                        <subItem.icon className="size-4" />
                                      )}
                                      <span>{subItem.title}</span>
                                    </a>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    )
                  }

                  // Simple item without subItems
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.url)}
                        tooltip={item.title}
                      >
                        <a
                          href={item.url}
                          onClick={(e) => {
                            e.preventDefault()
                            history.push(item.url)
                          }}
                        >
                          {item.icon && <item.icon className="size-4" />}
                          <span>{item.title}</span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={userData} onLogout={onLogout} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
